import { useAsyncFn } from 'react-use';

import { HttpStatusCodes } from '@service-initiation/shared-clients-http';
import { LoggerWarning } from '@service-initiation/shared-clients-logger';
import { logger } from '@service-initiation/shared-clients-osp-logger';
import { type CreateStoreAppointmentRequest } from '@service-initiation/shared-clients-tech-care-services';
import {
  DeliveryMethod,
  companyIdAtom,
  customerDetailsAtom,
  deliveryMethodAsyncAtom,
  deviceDetailsAsyncAtom,
  leadIdAtom,
  repairMessageAtom,
  selectedAppointmentAsyncAtom,
  selectedRepairsAsyncAtom,
  selectedStoreAsyncAtom,
  store,
  updatedLeadIdAtom,
} from '@service-initiation/tech-care-data-access-store';
import {
  createStoreAppointment,
  updateAppointment,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import { convertTimeSlotToTimestamp } from '@service-initiation/tech-care-utils-date-time';

import { appendCustomerMessage, buildExtraDataForLog } from '../utils';

export const useCreateAppointment = () => {
  return useAsyncFn(async () => {
    const selectedRepairs = await store.get(selectedRepairsAsyncAtom);
    const companyId = store.get(companyIdAtom);
    const repairMessage = store.get(repairMessageAtom);
    const customerDetails = store.get(customerDetailsAtom);
    const deviceDetails = await store.get(deviceDetailsAsyncAtom);
    const appointment = await store.get(selectedAppointmentAsyncAtom);
    const appointmentIsAsap = appointment.isAsap;
    const appointmentDate = appointment?.date;
    const appointmentEndTime = appointment?.endTime;
    const appointmentIntentId = appointment?.intentId;
    const appointmentStartTime = appointment?.startTime;
    const selectedStore = await store.get(selectedStoreAsyncAtom);
    const deliveryMethod = await store.get(deliveryMethodAsyncAtom);
    const updatedLeadId = store.get(updatedLeadIdAtom);

    if (deliveryMethod === DeliveryMethod.VisitStore) {
      if (
        !selectedStore?.id ||
        !customerDetails?.firstName ||
        !customerDetails?.lastName ||
        !customerDetails?.email ||
        !customerDetails?.phoneNumber ||
        !selectedStore?.time_zone ||
        !deviceDetails?.id ||
        (!selectedRepairs?.[0]?.id && !repairMessage && !companyId) ||
        (!appointmentIsAsap &&
          ((!appointmentIntentId && !appointmentDate) ||
            !appointmentStartTime ||
            !appointmentEndTime))
      ) {
        const error = new Error(
          'missing userSelection props when trying to createAppointment'
        );
        const extraData = await buildExtraDataForLog();
        logger.error(error, extraData);
        throw error;
      }

      const timeStamp = appointmentIsAsap
        ? undefined
        : convertTimeSlotToTimestamp(
            appointmentDate || '',
            appointmentStartTime || '',
            selectedStore?.time_zone || ''
          );

      if (updatedLeadId) {
        const updateResult = await updateAppointment({
          appointmentDateAndTimeUTC: timeStamp,
          companyId,
          country: 'US',
          countryCode: 'us',
          customer: {
            email: customerDetails.email,
            firstName: customerDetails.firstName,
            lastName: customerDetails.lastName,
            phoneNumber: customerDetails?.phoneNumber,
          },
          customerMessage: appendCustomerMessage(
            repairMessage,
            selectedRepairs
          ),
          deviceRepairId: selectedRepairs?.[0]?.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          deviceTypeId: deviceDetails!.id,
          intentId: appointmentIntentId,
          storeId: selectedStore.id,
          subscribeToMarketingEmails: customerDetails?.agreeToEmailOffers,
          updatedLeadId: updatedLeadId,
        });
        if (
          updateResult.status === HttpStatusCodes.ResetContent ||
          updateResult.status === HttpStatusCodes.OK
        ) {
          store.set(leadIdAtom, updatedLeadId);
        } else {
          const extraData = await buildExtraDataForLog();
          logger.warn(
            new LoggerWarning(
              `Failed to update appointment`,
              'error response from server'
            ),
            extraData
          );
        }
      } else {
        const createAppointmentResult = await createStoreAppointment({
          appointmentDateAndTimeUTC: timeStamp,
          companyId,
          customerDetails: {
            email: customerDetails?.email,
            firstName: customerDetails?.firstName,
            lastName: customerDetails?.lastName,
            phoneNumber: customerDetails?.phoneNumber,
          },
          customerMessage: appendCustomerMessage(
            repairMessage,
            selectedRepairs
          ),
          deviceRepairId: selectedRepairs?.[0]?.id,
          deviceTypeId: deviceDetails?.id,
          intentId: appointmentIntentId,
          storeId: selectedStore?.id,
          subscribeToMarketingEmails: customerDetails?.agreeToEmailOffers,
        } as CreateStoreAppointmentRequest);

        if (createAppointmentResult?.leadId) {
          store.set(leadIdAtom, createAppointmentResult.leadId);
        } else {
          const extraData = await buildExtraDataForLog();
          logger.warn(
            new LoggerWarning(
              `Failed to create appointment`,
              'error response from server'
            ),
            extraData
          );
        }
      }
    }
  });
};
